/**
 * TypeScript types mirroring CSC_CORE Receiving DTOs and enums.
 */
import type { PurchaseOrderDto, PurchaseOrderLineDto } from '@features/purchase-orders/types/purchaseOrder.types';
export type { PurchaseOrderDto, PurchaseOrderLineDto };
import { DocumentStatus } from '@shared/types';
export { DocumentStatus };

export enum ReceivingStatusEnum {
  PendingPutAway = 0,
  Completed = 1,
  Cancelled = 2,
  PartiallyPutAway = 3,
  PutAwayComplete = 4,
}

/** Async Item Receipt posting lifecycle (86d3kwtar) — orthogonal to ReceivingStatus. */
export enum PostingStatusEnum {
  None = 0,
  Posting = 1,
  PostingFailed = 2,
}

export enum DeliveryTypeEnum {
  Local = 0,
  ImportRawMaterials = 1,
  ImportSpareParts = 2,
  Projects = 3,
}

export enum ReceivingItemTypeEnum {
  InventoryItem = 0,
  LotNumbered = 1,
  Serialized = 2,
}

export enum PpeComplianceEnum {
  Yes = 1,
  No = 2,
  NotApplicable = 3,
}

export interface PurchaseOrderSuggestionDto {
  netSuitePOId: string;
  poNumber: string;
  vendorName: string;
}

export interface PagedPurchaseOrderDto {
  items: PurchaseOrderDto[];
  total: number;
  page: number;
  pageSize: number;
}

export interface BinAssignmentResultDto {
  binId: number;
  binCode: string;
  quantity: number;
  lotNumber?: string;
  expirationDate?: string;
  serialNumber?: string;
}

export enum PutAwayHistoryStatusEnum {
  Active = 0,
  Reversed = 1,
}

export interface PutAwayHistoryDto {
  id: number;
  receivingDocumentItemId: number;
  quantity: number;
  binCode: string;
  binId: number;
  locationId: number;
  lotNumber?: string;
  expirationDate?: string;
  serialNumber?: string;
  inventoryNumberId?: number;
  putAwayById: number;
  putAwayByName: string;
  putAwayAt: string;
  notes?: string;
  isPosted: boolean;

  /** Lifecycle status. Active rows count toward QuantityPutAway; Reversed rows are kept for audit. */
  status: PutAwayHistoryStatusEnum;
  /** Cumulative quantity reversed from this row. When equal to `quantity`, status is Reversed. */
  quantityReversed: number;
  reversedAt?: string;
  reversedById?: number;
  reversedByName?: string;
  reversedByAdjustmentLogId?: number;
}

export interface ReceivingDocumentItemDto {
  id: number;
  receivingDocumentId: number;
  netsuiteItemId: number;
  itemCode: string;
  itemDescription: string;
  purchaseOrderQuantity: number;
  receivedQuantity: number;
  quantityPutAway: number;
  unitOfMeasure: string;
  itemType: ReceivingItemTypeEnum;
  notes?: string;
  isPutAway: boolean;
  isServiceItem: boolean;
  binAssignments: BinAssignmentResultDto[];
  putAwayHistories: PutAwayHistoryDto[];
}

export interface ReceivingDocumentDto {
  id: number;
  docNo: string;
  documentStatus: DocumentStatus;
  receivingStatus: ReceivingStatusEnum;
  forAdjustment: boolean;
  ppeCompliance: PpeComplianceEnum;
  purchaseOrderId?: number;
  netSuitePurchaseOrderId: string;
  purchaseOrderNumber: string;
  vendorName: string;
  deliveryNumber: string;
  deliveryType: DeliveryTypeEnum;
  docDate?: string;
  timeIn?: string;
  timeOut?: string;
  letterOfCredit?: string;
  vesselName?: string;
  billOfLading?: string;
  remarks?: string;
  receivedById: number;
  receivedByName: string;
  /** Stage 1 receiver's NetSuite Employee ID. Null when not linked — frontend uses this to disable the "Post to NetSuite" button. */
  receivedByNetSuiteEmployeeId: string | null;
  receivedAt: string;
  createdAt: string;
  putAwayCompletedAt?: string;
  netSuiteItemReceiptId?: string;
  netSuiteItemReceiptTranId?: string;
  netSuiteItemReceiptUrl?: string;
  /** Async posting lifecycle (86d3kwtar) — drives the "Posting…" card + the poll; PostingFailed carries the reason. */
  postingStatus: PostingStatusEnum;
  postingFailedReason?: string;
  /** 86d3mnuxe FIX 3 — how far the failed attempt got toward NetSuite (PostReach numeric; ≥2 = an
   *  Item Receipt may exist). Drives the banner's Retry-vs-Reconcile label + hides Cancel when reached. */
  postingReach?: number;
  /** The NetSuite internal id the failed attempt created, when known. */
  postingNetSuiteRecordId?: string | null;
  closedAt?: string;
  closedByName?: string;
  cancelledAt?: string;
  cancelledByName?: string;
  items: ReceivingDocumentItemDto[];
}

export interface BinOptionDto {
  binId: number;
  binCode: string;
  locationId: number;
  currentStock: number;
  inventoryNumber?: string;
  inventoryNumberId?: number;
}

export interface BinAssignmentRequest {
  binId: number;
  binCode: string;
  locationId: number;
  quantity: number;
  lotNumber?: string;
  expirationDate?: string;
  inventoryNumberId?: number;
  serialNumber?: string;
}

export interface PutAwayItemRequest {
  receivingDocumentItemId: number;
  netsuiteItemId: number;
  itemCode: string;
  unitOfMeasure: string;
  totalReceivedQuantity: number;
  binAssignments: BinAssignmentRequest[];
}

export interface CompletePutAwayRequest {
  items: PutAwayItemRequest[];
}

export interface CreateReceivingDocumentItemRequest {
  purchaseOrderItemId?: number;
  netsuiteItemId: number;
  itemCode: string;
  itemDescription: string;
  purchaseOrderQuantity: number;
  receivedQuantity: number;
  unitOfMeasure: string;
  itemType: ReceivingItemTypeEnum;
  notes?: string;
}

export interface CreateReceivingDocumentRequest {
  purchaseOrderId?: number;
  netSuitePurchaseOrderId: string;
  purchaseOrderNumber: string;
  vendorName: string;
  deliveryNumber: string;
  deliveryType: DeliveryTypeEnum;
  docDate?: string;
  timeIn?: string;
  timeOut?: string;
  letterOfCredit?: string;
  vesselName?: string;
  billOfLading?: string;
  remarks?: string;
  ppeCompliance: PpeComplianceEnum;
  items: CreateReceivingDocumentItemRequest[];
}

export interface PostReceivingDocumentRequest {
  ppeCompliance: PpeComplianceEnum;
}

export interface CancelReceivingDocumentRequest {
  reason?: string;
}

export enum ReceivingAdjustmentReasonEnum {
  CountError = 0,
  DamageFound = 1,
  ShortShipment = 2,
  SupplierError = 3,
  Other = 4,
  // 86d3tdbnn — correction-sheet reasons (mirror CSC_CORE ReceivingAdjustmentReasonEnum; the server
  // rejects values outside the enum via IsInEnum).
  WrongBin = 5,
  WrongSerial = 6,
  DuplicateEntry = 7,
}

/**
 * Bin-level reversal — caller specifies which PutAwayHistory entry to give back and how much.
 * REQUIRED inside EditReceivedQuantityRequest.binReversals when reducing newReceivedQuantity
 * below the item's current quantityPutAway.
 */
export interface PutAwayReversalDto {
  putAwayHistoryId: number;
  quantityToReverse: number;
}

/**
 * 86d3tdbnn — in-place serial rename for a serialized put-away row (typo fix). Net-zero on placement.
 * Removing a serial is NOT a serial edit — send it as a qty-1 entry in `binReversals`.
 */
export interface SerialEditDto {
  putAwayHistoryId: number;
  newSerialNumber: string;
}

export interface EditReceivedQuantityRequest {
  newReceivedQuantity: number;
  reason: ReceivingAdjustmentReasonEnum;
  notes?: string;

  /** Bin/serial reversals (serial removal = a qty-1 reversal). Required to keep placed ≤ received. */
  binReversals?: PutAwayReversalDto[];
  /** 86d3tdbnn — in-place serial renames (serialized items only). */
  serialEdits?: SerialEditDto[];
  /**
   * @deprecated 86d3tdbnn — the correction sheet no longer collects this (out of scope). The field
   * stays for API compatibility; the web UI sends it undefined.
   */
  externalReference?: string;
}

export interface ReceivingDocumentResyncResultDto {
  success: boolean;
  message: string;
  errors: string[];
}

// Table row types for expandable TanStack Table (mirrors picklist pattern)
export interface ReceivingParentRow {
  rowType: 'document';
  id: string;
  document: ReceivingDocumentDto;
  subRows: ReceivingItemRow[];
}

export interface ReceivingItemRow {
  rowType: 'item';
  id: string;
  documentId: number;
  item: ReceivingDocumentItemDto;
}

export type ReceivingTableRow = ReceivingParentRow | ReceivingItemRow;
